import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight, Clock, FileText, Folder, MagnifyingGlass,
  NotePencil, Palette, Presentation, Table,
} from '@phosphor-icons/react';
import { Icon } from '../../Icon/Icon';
import FileIcon from '../../FileIcon/FileIcon';
import { AppSquircle } from '../../AppSquircle';
import type { BaseEntityType } from '../../../types/entities';
import { EntityTypeUtils } from '../../../types/entities';
import useEntityStore from '../../../stores/entityStore';
import useSpaceStore from '../../../stores/spaceStore';
import useDataspaceTreeStore from '../../../stores/dataspaceTreeStore';
import { useCommandRegistryStore } from '../../../stores/commandRegistryStore';
import { entityService } from '../../../services/entityService';
import type { EntitySearchResult } from '../../../services/entityService';
import { createFileLogger } from '../../../utils/logger';
import './GlobalEntitySearch.scss';

const log = createFileLogger('GLOBAL_ENTITY_SEARCH');

const ENTITY_TYPE_LABELS: Record<string, string> = {
  Doc: 'Document', Note: 'Note', Page: 'Page', File: 'File',
  Contact: 'Contact', Task: 'Task', Goal: 'Goal', Project: 'Project',
  Agent: 'Agent', Thread: 'Thread', Space: 'Space', Group: 'Folder',
};

function typeLabel(et: string): string {
  if (ENTITY_TYPE_LABELS[et]) return ENTITY_TYPE_LABELS[et];
  if (et.startsWith('File.')) return 'File';
  if (et.startsWith('Integration.')) return et.replace('Integration.', '');
  return et;
}

function getEntityIcon(entity: BaseEntityType): React.ReactNode {
  if (entity.entityType === 'Group') return <Folder size={16} weight="fill" />;
  if (EntityTypeUtils.isSubtypeOf(entity.entityType, 'File') && entity.name) {
    return <FileIcon filename={entity.name} entityType={entity.entityType} size={16} />;
  }
  return <Icon entity={entity} size="small" />;
}

// ── Quickbar item definitions ─────────────────────────────────────────────────

interface QuickbarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const CREATE_ITEMS: QuickbarItem[] = [
  { id: 'new-doc',    label: 'Doc',    icon: <FileText    size={16} weight="duotone" style={{ color: '#2563eb' }} /> },
  { id: 'new-note',   label: 'Note',   icon: <NotePencil  size={16} weight="duotone" style={{ color: '#f59e0b' }} /> },
  { id: 'new-canvas', label: 'Canvas', icon: <Palette     size={16} weight="duotone" style={{ color: '#9333ea' }} /> },
  { id: 'new-sheet',  label: 'Sheet',  icon: <Table       size={16} weight="duotone" style={{ color: '#10b981' }} /> },
  { id: 'new-slides', label: 'Slides', icon: <Presentation size={16} weight="duotone" style={{ color: '#f97316' }} /> },
];

const APP_ITEMS: QuickbarItem[] = [
  { id: 'app-drive',       label: 'Drive',       icon: <AppSquircle appId="entityBrowser2" size="sm" /> },
  { id: 'app-automations', label: 'Automations', icon: <AppSquircle appId="automations"    size="sm" /> },
  { id: 'app-contacts',    label: 'Contacts',    icon: <AppSquircle appId="contacts"       size="sm" /> },
  { id: 'app-mail',        label: 'Mail',        icon: <AppSquircle appId="mail"           size="sm" /> },
];

const QUICKBAR_ITEMS: QuickbarItem[] = [...CREATE_ITEMS, ...APP_ITEMS];

// ── Component ─────────────────────────────────────────────────────────────────

interface GlobalEntitySearchProps {
  onClose: () => void;
}

type ActiveZone = 'quickbar' | 'results' | null;

const GlobalEntitySearch: React.FC<GlobalEntitySearchProps> = ({ onClose }) => {
  const [inputValue, setInputValue] = useState('');
  const [activeZone, setActiveZone] = useState<ActiveZone>(null);
  const [quickbarIndex, setQuickbarIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [backendResults, setBackendResults] = useState<EntitySearchResult[]>([]);
  const [backendLoading, setBackendLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const quickbarRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const navigate = useNavigate();
  const { executeCommand } = useCommandRegistryStore();
  const activeSpace = useSpaceStore(s => s.activeSpace);
  const currentSpaceId = activeSpace?.id;
  const hasActiveSpace = !!activeSpace;
  const allEntities = useEntityStore(s => s.entities);

  // Creates a minimal space and navigates to it with an optional URL suffix.
  // Used when the modal is triggered from outside a space (e.g. Home page).
  const createSpaceAndNavigate = useCallback(async (urlSuffix: string, spaceName: string) => {
    try {
      const { default: useSystemStore } = await import('../../../stores/systemStore');
      const newSpace = await useSystemStore.getState().actions.createSpace({
        name: spaceName,
        skipDefaultWindow: true,
        skipProjectLimit: true,
        settings: {
          isChatVisible: false,
          viewMode: 'focused',
          sidebar: { isVisible: true, width: 300, mode: 'normal' },
        },
      });
      if (newSpace?.id) {
        navigate(`/spaces/${newSpace.id}${urlSuffix}`);
      }
    } catch (err) {
      log.error('[GlobalEntitySearch] Failed to create space:', err);
    }
  }, [navigate]);
  const getNode = useDataspaceTreeStore(s => s.getNode);

  // Build id → name map for parent path lookup
  const entityNameById = useMemo(() => {
    const map = new Map<string, string>();
    allEntities.forEach(e => { if (e._id) map.set(e._id, e.name || ''); });
    return map;
  }, [allEntities]);

  const getParentName = useCallback((entityId: string): string | null => {
    const node = getNode(entityId);
    if (!node?.parentId) return null;
    return entityNameById.get(node.parentId) || null;
  }, [getNode, entityNameById]);

  // Client-side results
  const clientResults = useMemo((): BaseEntityType[] => {
    if (!inputValue.trim()) {
      return [...allEntities]
        .filter(e => !e.trashed && !(e as any).hidden)
        .sort((a, b) => {
          const da = new Date(a.updated_at || a.created_at || 0).getTime();
          const db = new Date(b.updated_at || b.created_at || 0).getTime();
          return db - da;
        })
        .slice(0, 10);
    }
    const q = inputValue.toLowerCase();
    return allEntities
      .filter(e =>
        !e.trashed && !(e as any).hidden &&
        (e.name?.toLowerCase().includes(q) || e.entityType?.toLowerCase().includes(q))
      )
      .slice(0, 12);
  }, [allEntities, inputValue]);

  // Debounced backend search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!inputValue.trim() || inputValue.trim().length < 2) {
      abortRef.current?.abort();
      setBackendResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setBackendLoading(true);
      try {
        const results = await entityService.searchEntities(inputValue.trim(), undefined, 20, false, ac.signal);
        if (abortRef.current !== ac) return;
        setBackendResults(results);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setBackendResults([]);
      } finally {
        if (abortRef.current === ac) setBackendLoading(false);
      }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [inputValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Dedup backend extras against client results
  const clientIds = useMemo(() => new Set(clientResults.map(e => e._id)), [clientResults]);
  const backendExtras = useMemo((): EntitySearchResult[] => {
    if (!inputValue.trim()) return [];
    return backendResults.filter(r => !clientIds.has(r.entity_id));
  }, [backendResults, clientIds, inputValue]);

  const totalCount = clientResults.length + backendExtras.length;

  // Reset zone & selection on query change
  useEffect(() => {
    setActiveZone(null);
    setSelectedIndex(0);
  }, [inputValue]);

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Scroll selected result into view
  useEffect(() => {
    if (activeZone === 'results') {
      listRef.current?.querySelector('.global-entity-search-item.selected')
        ?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, activeZone]);

  // Scroll focused quickbar item into view
  useEffect(() => {
    if (activeZone === 'quickbar') {
      quickbarRef.current?.querySelector('.quickbar-item.focused')
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [quickbarIndex, activeZone]);

  // ── Quickbar actions ──────────────────────────────────────────────────────

  const fireQuickbarItem = useCallback(async (item: QuickbarItem) => {
    onClose();
    const spaceId = currentSpaceId;

    // App items — when no active space, create one and navigate with ?openApp=
    const APP_ID_MAP: Record<string, string> = {
      'app-drive':       'entityBrowser2',
      'app-automations': 'automations',
      'app-contacts':    'contacts',
      'app-mail':        'mail',
    };
    const appId = APP_ID_MAP[item.id];
    if (appId) {
      if (!hasActiveSpace) {
        sessionStorage.setItem('pendingAppOpen', JSON.stringify({ appId, appName: item.label }));
        createSpaceAndNavigate(`?openApp=${appId}`, item.label);
      } else {
        const windowId = await useSpaceStore.getState().actions.createWindow({ appType: appId });
        useSpaceStore.getState().setActiveWindow(windowId);
      }
      return;
    }

    // Create items — when no active space, store intent and create space
    const CREATE_COMMAND_MAP: Record<string, string> = {
      'new-doc':    'doc',
      'new-note':   'note',
      'new-canvas': 'canvas',
      'new-sheet':  'sheet',
      'new-slides': 'slides',
    };
    const createType = CREATE_COMMAND_MAP[item.id];
    if (createType) {
      if (!hasActiveSpace) {
        sessionStorage.setItem('pendingEntityCreate', JSON.stringify({ type: createType }));
        createSpaceAndNavigate('', item.label);
      } else {
        let result: any;
        if (item.id === 'new-doc') {
          result = await executeCommand('docs_create', { name: 'New Document', spaceId, createWindow: true });
        } else if (item.id === 'new-note') {
          result = await executeCommand('notes_create', { title: 'Untitled', spaceId, createWindow: true });
        } else if (item.id === 'new-canvas') {
          result = await executeCommand('canvas_create', { title: 'Untitled Canvas', spaceId, createWindow: true });
        } else if (item.id === 'new-sheet') {
          result = await executeCommand('sheets_create', { title: 'New Sheet', spaceId, createWindow: true });
        } else if (item.id === 'new-slides') {
          result = await executeCommand('presentations_create', { title: 'New Presentation', spaceId, createWindow: true });
        }
        const windowId = result?.data?.window?.id;
        if (windowId) {
          useSpaceStore.getState().setActiveWindow(windowId);
        }
      }
    }
  }, [executeCommand, onClose, currentSpaceId, hasActiveSpace, createSpaceAndNavigate]);

  // ── Entity selection ──────────────────────────────────────────────────────

  const handleSelectClient = useCallback((entity: BaseEntityType) => {
    log.log('[GlobalEntitySearch] Entity selected:', entity.name, entity.entityType);
    onClose();

    // No active space — open entity by creating a new space and navigating there
    if (!hasActiveSpace) {
      createSpaceAndNavigate(`?openEntity=${entity._id}`, entity.name || 'Space');
      return;
    }

    if (
      entity.entityType === 'Group' ||
      entity.entityType === 'Integration' ||
      entity.entityType?.startsWith('Integration.')
    ) {
      executeCommand('space_createWindow', {
        appType: 'entityBrowser2',
        title: entity.name || 'Folder',
        entity,
        callback: (result: string) => {
          try {
            const parsed = JSON.parse(result);
            if (parsed.success) log.log('[GlobalEntitySearch] EntityBrowser opened:', parsed);
          } catch (err) {
            log.error('[GlobalEntitySearch] Failed to parse space_createWindow result:', err);
          }
        },
      });
    } else {
      executeCommand('space_open_window', {
        entityId: entity._id,
        callback: (result: string) => {
          try {
            const parsed = JSON.parse(result);
            if (!parsed.success) log.error('[GlobalEntitySearch] Failed to open entity:', parsed.error);
          } catch (err) {
            log.error('[GlobalEntitySearch] Failed to parse space_open_window result:', err);
          }
        },
      });
    }
  }, [executeCommand, onClose, hasActiveSpace, createSpaceAndNavigate]);

  const handleSelectBackend = useCallback((result: EntitySearchResult) => {
    const full = allEntities.find(e => e._id === result.entity_id);
    if (full) {
      handleSelectClient(full);
      return;
    }

    onClose();

    if (!hasActiveSpace) {
      createSpaceAndNavigate(`?openEntity=${result.entity_id}`, result.name || 'Space');
      return;
    }

    executeCommand('space_open_window', {
      entityId: result.entity_id,
      callback: (res: string) => {
        try {
          const parsed = JSON.parse(res);
          if (!parsed.success) log.error('[GlobalEntitySearch] Failed to open backend entity:', parsed.error);
        } catch (err) {
          log.error('[GlobalEntitySearch] Failed to parse result:', err);
        }
      },
    });
  }, [allEntities, handleSelectClient, onClose, executeCommand, hasActiveSpace, createSpaceAndNavigate]);

  // ── Keyboard navigation ───────────────────────────────────────────────────

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (activeZone === null) {
        setActiveZone('quickbar');
        setQuickbarIndex(0);
      } else if (activeZone === 'quickbar') {
        setActiveZone('results');
        setSelectedIndex(0);
      } else {
        setSelectedIndex(i => Math.min(i + 1, totalCount - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (activeZone === 'results' && selectedIndex === 0) {
        setActiveZone('quickbar');
        setQuickbarIndex(0);
      } else if (activeZone === 'results') {
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (activeZone === 'quickbar') {
        setActiveZone(null);
      }
    } else if (e.key === 'ArrowLeft' && activeZone === 'quickbar') {
      e.preventDefault();
      setQuickbarIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'ArrowRight' && activeZone === 'quickbar') {
      e.preventDefault();
      setQuickbarIndex(i => Math.min(i + 1, QUICKBAR_ITEMS.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeZone === 'quickbar') {
        const item = QUICKBAR_ITEMS[quickbarIndex];
        if (item) fireQuickbarItem(item);
      } else if (activeZone === 'results') {
        if (selectedIndex < clientResults.length) {
          handleSelectClient(clientResults[selectedIndex]);
        } else {
          const be = backendExtras[selectedIndex - clientResults.length];
          if (be) handleSelectBackend(be);
        }
      } else {
        // No zone active — fire first result
        if (clientResults.length > 0) {
          handleSelectClient(clientResults[0]);
        } else if (backendExtras.length > 0) {
          handleSelectBackend(backendExtras[0]);
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [
    activeZone, quickbarIndex, selectedIndex, totalCount,
    clientResults, backendExtras,
    fireQuickbarItem, handleSelectClient, handleSelectBackend, onClose,
  ]);

  const isEmpty = !inputValue.trim();
  const hasResults = totalCount > 0;

  return (
    <div className="global-entity-search-overlay" onClick={onClose}>
      <div className="global-entity-search" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="global-entity-search-header">
          <MagnifyingGlass size={20} className="search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="global-entity-search-input"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search everything..."
            autoComplete="off"
            spellCheck={false}
          />
          {backendLoading && <span className="search-spinner" />}
        </div>

        {/* ── Quickbar ── */}
        <div
          className={`global-entity-search-quickbar${activeZone === 'quickbar' ? ' is-active' : ''}`}
          ref={quickbarRef}
        >
          <div className="quickbar-creates">
            {CREATE_ITEMS.map((item, i) => (
              <button
                key={item.id}
                className={`quickbar-item${activeZone === 'quickbar' && quickbarIndex === i ? ' focused' : ''}`}
                onMouseDown={e => { e.preventDefault(); fireQuickbarItem(item); }}
                onMouseEnter={() => { setActiveZone('quickbar'); setQuickbarIndex(i); }}
                tabIndex={-1}
                title={item.label}
              >
                <span className="quickbar-item-icon">{item.icon}</span>
                <span className="quickbar-item-label">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="quickbar-divider" />
          <div className="quickbar-apps">
            {APP_ITEMS.map((item, i) => {
              const globalIdx = CREATE_ITEMS.length + i;
              return (
                <button
                  key={item.id}
                  className={`quickbar-item${activeZone === 'quickbar' && quickbarIndex === globalIdx ? ' focused' : ''}`}
                  onMouseDown={e => { e.preventDefault(); fireQuickbarItem(item); }}
                  onMouseEnter={() => { setActiveZone('quickbar'); setQuickbarIndex(globalIdx); }}
                  tabIndex={-1}
                  title={item.label}
                >
                  <span className="quickbar-item-icon">{item.icon}</span>
                  <span className="quickbar-item-label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Results ── */}
        <div className="global-entity-search-results" ref={listRef}>
          {!hasResults && !isEmpty ? (
            <div className="global-entity-search-empty">Nothing found for "{inputValue}"</div>
          ) : !hasResults && isEmpty ? (
            <div className="global-entity-search-empty">No entities available</div>
          ) : (
            <>
              {isEmpty && (
                <div className="search-section-label">
                  <Clock size={11} weight="bold" /> Recent
                </div>
              )}

              {clientResults.map((entity, i) => {
                const parentName = getParentName(entity._id);
                return (
                  <div
                    key={entity._id}
                    className={`global-entity-search-item ${activeZone === 'results' && i === selectedIndex ? 'selected' : ''}`}
                    onClick={() => handleSelectClient(entity)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(i); }}
                  >
                    <div className="entity-icon">{getEntityIcon(entity)}</div>
                    <div className="entity-content">
                      <div className="entity-name">{entity.name || 'Unnamed'}</div>
                      {parentName && <div className="entity-path">in {parentName}</div>}
                    </div>
                    <span className="entity-type-label">{typeLabel(entity.entityType || '')}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}

              {backendExtras.length > 0 && (
                <div className="search-section-label">Content matches</div>
              )}

              {backendExtras.map((result, i) => {
                const idx = clientResults.length + i;
                return (
                  <div
                    key={result.entity_id}
                    className={`global-entity-search-item ${activeZone === 'results' && idx === selectedIndex ? 'selected' : ''}`}
                    onClick={() => handleSelectBackend(result)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(idx); }}
                  >
                    <div className="entity-icon">
                      <Icon entity={{ entityType: result.entityType, name: result.name } as any} size="small" />
                    </div>
                    <div className="entity-content">
                      <div className="entity-name">{result.name}</div>
                      {result.snippet && <div className="entity-path">{result.snippet}</div>}
                    </div>
                    <span className="entity-type-label">{typeLabel(result.entityType)}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="global-entity-search-footer">
          <div className="keyboard-hints">
            <div className="hint">
              <span className="key">↑↓</span>
              <span>Navigate</span>
            </div>
            <div className="hint">
              <span className="key">←→</span>
              <span>Quickbar</span>
            </div>
            <div className="hint">
              <span className="key">↵</span>
              <span>Open</span>
            </div>
            <div className="hint">
              <span className="key">esc</span>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalEntitySearch;
